import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { browserSupportsWebAuthnAutofill, startAuthentication, WebAuthnAbortService } from "@simplewebauthn/browser";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { safeRedirectTarget } from "../lib/redirectTarget";
import { homePathForRole } from "../lib/homePath";
import { SESSION_ENDED_MESSAGE, sessionEndedRequested } from "../lib/sessionExpiry";
import { CompassLogo } from "../components/CompassLogo";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

export default function Login() {
  const { user, loginResult, completePasskeySignIn } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  // Issue #257. Where this sign-in is FOR: the page the visitor was actually
  // trying to open when `ProtectedRoute` sent them here, or the dashboard if
  // they simply came to sign in. Validated, never trusted — see
  // `lib/redirectTarget.js` for why history state is hostile input.
  // The fallback is role-aware since #384 item 3: a coach with no captured
  // link belongs on My Players, not on a dashboard their role can no longer
  // open. `user` is false on the sign-in render and set on the one after,
  // which is exactly when this branch's fallback starts to matter.
  const target = safeRedirectTarget(location.state?.from, homePathForRole(user?.role));
  // Issue #396. Set when the app got here because a request came back 401 —
  // i.e. the session ended underneath a tab that was still rendering as
  // signed in. Without this the user is dropped on a sign-in form with no
  // explanation, which reads as the app having lost their work.
  //
  // A boolean derived from a fixed sentinel (`?session=expired`), never the
  // parameter's own text: the message below is a constant in
  // `lib/sessionExpiry.js`, so nothing a link can carry is ever rendered
  // here. A query param that printed itself would be an invitation to craft
  // `/login?session=Your%20account%20was%20suspended…`.
  const sessionEnded = sessionEndedRequested(location.search);
  // Issue #27: no longer prefilled with a demo account — a prototype-era
  // prefill on the actual sign-in fields is misleading once real auth is
  // deployed. D205 (#1023) removed the "Explore as…" buttons entirely:
  // there is no public demo door any more.
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  // Issue #515: "Keep me signed in on this device". DEFAULT OFF, and per
  // sign-in — never persisted, never prefilled — so the 30-day session is
  // only ever the result of a tick made right now, on this device, next to
  // the shared-device warning below. Unticked, the sign-in is byte-identical
  // to before #515 (a 24h session).
  const [remember, setRemember] = useState(false);
  const [busy, setBusy] = useState(false);
  // Issue #561: the passkey door. Feature-detected like PasskeysCard — an
  // unsupported browser gets no button rather than a doomed ceremony.
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const passkeySupported = typeof window !== "undefined" && !!window.PublicKeyCredential;
  // Guards the conditional-UI effect's late resolutions: a ceremony that
  // completes after unmount (or after another flow finished) must not
  // navigate a page that is no longer there.
  const mountedRef = useRef(true);

  // The tail both passkey flows share: exchange the browser's assertion for
  // a session via AuthContext (the completeSignInLink precedent), then land
  // exactly where a password login would — same safeRedirectTarget, same
  // role-aware fallback. The server preserves must_change_password, so a
  // confined account is routed to the forced-change screen by the same
  // context/redirect logic, nothing passkey-specific here.
  const finishPasskeySignIn = async (assertion) => {
    const signedIn = await completePasskeySignIn(assertion);
    if (!mountedRef.current) return;
    if (signedIn) {
      toast.success("Welcome back!");
      nav(safeRedirectTarget(location.state?.from, homePathForRole(signedIn.role)), { replace: true });
    } else {
      toast.error("Passkey sign-in failed. Try again or use your password.");
    }
  };

  // Issue #561: CONDITIONAL UI — started on mount, so the browser can offer
  // saved passkeys in the email field's autofill (the input carries
  // autocomplete="username webauthn"). Two WebAuthn ceremonies cannot run at
  // once (the second throws), so every other path that starts one — the
  // explicit button below, or leaving the page — first cancels this pending
  // ceremony via WebAuthnAbortService (the library's own AbortController
  // wrapper; cancelCeremony() aborts the outstanding ceremony's controller).
  useEffect(() => {
    mountedRef.current = true;
    let cancelled = false;
    (async () => {
      try {
        if (!passkeySupported || !(await browserSupportsWebAuthnAutofill())) return;
        const { data: options } = await api.post("/auth/passkey/authenticate/options", {});
        if (cancelled) return;
        const assertion = await startAuthentication({ optionsJSON: options, useBrowserAutofill: true });
        await finishPasskeySignIn(assertion);
      } catch {
        // AbortError (we cancelled it) or NotAllowedError (the user
        // dismissed the browser's own sheet): their act or ours, no toast.
        // Anything else (e.g. options fetch failed) stays silent too — the
        // conditional ceremony is an OFFER; the page's other doors all work.
      }
    })();
    return () => {
      mountedRef.current = false;
      cancelled = true;
      WebAuthnAbortService.cancelCeremony();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The explicit button. startAuthentication would cancel the pending
  // conditional ceremony itself, but doing it explicitly keeps "one ceremony
  // at a time" a property of this page, not of a library internal.
  const signInWithPasskey = async () => {
    setPasskeyBusy(true);
    try {
      WebAuthnAbortService.cancelCeremony();
      const { data: options } = await api.post("/auth/passkey/authenticate/options", {});
      const assertion = await startAuthentication({ optionsJSON: options });
      await finishPasskeySignIn(assertion);
    } catch (e) {
      if (e?.name !== "NotAllowedError" && e?.name !== "AbortError") {
        toast.error("Passkey sign-in failed. Try again or use your password.");
      }
    }
    if (mountedRef.current) setPasskeyBusy(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    // A password submit ends the passkey offer: the pending conditional
    // ceremony would otherwise still resolve later, over a signed-in page.
    WebAuthnAbortService.cancelCeremony();
    // The USER, not a boolean (AuthContext): this handler has to know the
    // role to know where to go, and the context value it closed over is
    // still the anonymous one at this point.
    // #1183: the reason rides in the result — `useAuth().error` here is the
    // render before this request (stale), so the toast always said "Login failed".
    const result = await loginResult(email, password, remember);
    const signedIn = result.ok ? result.user : false;
    setBusy(false);
    // `replace`, so the sign-in form is not left behind in history: pressing
    // Back from the destination would land on /login, which — now that a
    // session exists — immediately forwards here again, leaving Back looking
    // broken. Replacing it means Back goes wherever the visitor was before
    // they followed the link.
    if (signedIn) {
      toast.success("Welcome back!");
      nav(safeRedirectTarget(location.state?.from, homePathForRole(signedIn.role)), { replace: true });
    } else toast.error(result.error || "Login failed");
  };

  // A visitor who is already signed in has no business being handed a
  // sign-in form: submitting it silently replaces the session they arrived
  // with. `/` has had this guard since the landing page existed, and the
  // landing page links straight here, so the asymmetry was reachable from
  // the front door. After every hook above, so hook order stays
  // unconditional.
  //
  // `target` rather than a hardcoded /dashboard (#257): this branch also
  // catches the re-render immediately after a successful `login()` in the
  // same tick as `nav()` above, and sending that case to the dashboard would
  // silently discard the captured link in exactly the race the fix is about.
  if (user) return <Navigate to={target} replace />;

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      <div className="relative hidden lg:block overflow-hidden">
        <img
          src="https://images.pexels.com/photos/20652481/pexels-photo-20652481.jpeg"
          alt="Cricket"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(11,46,89,0.85) 0%, rgba(11,46,89,0.6) 100%)" }} />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <Link to="/" className="bg-white rounded-lg px-3 py-2 inline-flex self-start">
            <CompassLogo size={52} />
          </Link>
          <div>
            <h1 className="font-display font-black text-4xl sm:text-5xl leading-tight">Guiding every step of your cricket journey.</h1>
            {/* No AI coaching in the pitch: those surfaces are flagged off
                (lib/featureFlags.js, issue #56), so naming them here sells a
                page the account cannot open. */}
            <p className="mt-4 text-white/75 max-w-md">Log matches and training, watch a season's record build, and invite the coach you choose.</p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-flex lg:hidden mb-8" data-testid="login-home">
            <CompassLogo size={40} />
          </Link>
          <h2 className="font-display font-black text-3xl text-slate-900">Sign in</h2>
          <p className="text-slate-500 mt-1">
            Welcome back — let's continue your journey.{" "}
            <Link to="/" className="font-semibold underline" style={{ color: "#0B2E59" }} data-testid="link-landing">
              What is CricketCompass?
            </Link>
          </p>

          {sessionEnded && (
            <div
              className="mt-6 rounded-lg border border-slate-300 bg-slate-50 px-4 py-3 text-sm text-slate-700"
              role="status"
              data-testid="session-ended-notice"
            >
              {SESSION_ENDED_MESSAGE}
            </div>
          )}

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <Label>Email</Label>
              <Input data-testid="login-email" value={email} onChange={(e) => setEmail(e.target.value)} type="email" autoComplete="username webauthn" required className="mt-1.5 h-11" />
            </div>
            <div>
              <Label>Password</Label>
              <Input data-testid="login-password" value={password} onChange={(e) => setPassword(e.target.value)} type="password" required className="mt-1.5 h-11" />
            </div>
            {/* Issue #515. Opt-in, default off (see the state above). The
                warning is part of the control, not a tooltip: the moment of
                ticking is the one moment the shared-device trade-off can be
                explained to the person making it — the same "say what leaves
                this device, where the choice is made" posture as Register's
                guardian confirmation and Settings' usage disclosure. */}
            <label className="flex items-start gap-2 text-sm text-slate-600 cursor-pointer select-none">
              <Checkbox
                checked={remember}
                onCheckedChange={(v) => setRemember(v === true)}
                data-testid="login-remember-device"
                className="mt-0.5 shrink-0"
              />
              <span className="min-w-0">
                Keep me signed in on this device for 30 days.{" "}
                <span className="text-slate-500">
                  Don't tick this on a shared or family device — anyone who picks it up could open
                  this account. You can end it any time with "Sign out everywhere" in Settings.
                </span>
              </span>
            </label>
            <Button data-testid="login-submit" type="submit" disabled={busy} className="w-full h-11 text-base font-semibold" style={{ background: "#0B2E59" }}>
              {busy ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          {/* Issue #510: the passwordless door, beside the password form.
              A link to the request page rather than a second stacked form —
              the issue calls for one clean sign-in surface, not three
              forms. Passwords stay the primary door; this is an additional
              one. */}
          <div className="mt-3 flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
            {/* Issue #561: the passkey door lives in the SAME link row — one
                clean surface (#510's rule), an affordance beside the
                sign-in-link one, not a third stacked form. Rendered only
                where WebAuthn exists at all. */}
            {passkeySupported && (
              <button
                type="button"
                onClick={signInWithPasskey}
                disabled={busy || passkeyBusy}
                className="text-xs font-semibold text-slate-500 hover:text-slate-900 disabled:opacity-50"
                data-testid="login-passkey"
              >
                {passkeyBusy ? "Waiting for your passkey…" : "Sign in with a passkey"}
              </button>
            )}
            <Link to="/sign-in-link" className="text-xs font-semibold text-slate-500 hover:text-slate-900" data-testid="link-sign-in-link">
              Email me a sign-in link
            </Link>
            <Link to="/forgot-password" className="text-xs font-semibold text-slate-500 hover:text-slate-900" data-testid="link-forgot">
              Forgot your password?
            </Link>
          </div>

          <div className="mt-6 text-sm text-center text-slate-600">
            Don't have an account?{" "}
            {/* The captured destination travels with the visitor (#257): a
                shared link followed by someone who doesn't have an account
                yet leads through sign-up, and dropping the intent at this
                hop would lose exactly the first-time visitor the landing
                page exists for. */}
            <Link to="/register" state={location.state} className="font-semibold" style={{ color: "#0B2E59" }} data-testid="link-register">Create one</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
